<!-- eslint-disable max-len -->
<template>
  <div v-if="dgBillingRecord">
    <!-- Header -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-engine</v-icon>
        DG Billing Record Details
        <v-spacer />
        <v-chip
          :color="efficiencyStatusColor"
          text-color="white"
          class="mr-2"
        >
          <v-icon left small>{{ efficiencyStatusIcon }}</v-icon>
          {{ efficiencyStatusText }}
        </v-chip>
        <v-btn
          color="warning"
          class="mr-2"
          @click="editRecord"
        >
          <v-icon left>mdi-pencil</v-icon>
          Edit
        </v-btn>
        <v-btn
          color="primary"
          @click="exportRecord"
        >
          <v-icon left>mdi-download</v-icon>
          Export
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <div class="text-subtitle-2">Record ID</div>
            <div class="text-h6">{{ dgBillingRecord._id || dgBillingRecord.id }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="text-subtitle-2">Created</div>
            <div class="text-h6">{{ formatDateTime(dgBillingRecord.createdAt) }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Billing Period Information -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1 primary--text">
        <v-icon left>mdi-calendar-range</v-icon>
        Billing Period Information
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="primary">mdi-calendar-start</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Start Date</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(dgBillingRecord.billingPeriod.startDate) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col cols="12" md="4">
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="primary">mdi-calendar-end</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>End Date</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(dgBillingRecord.billingPeriod.endDate) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col cols="12" md="4">
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="info">mdi-calendar-clock</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Duration</v-list-item-title>
                <v-list-item-subtitle>{{ billingDuration }} days</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- DG Consumption Data -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1 success--text">
        <v-icon left>mdi-lightning-bolt</v-icon>
        DG Power Generation Data
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="success">mdi-gauge-empty</v-icon>
                <div class="text-h4 success--text mt-2">{{ formatNumber(dgBillingRecord.dgConsumptionData.initialReading) }}</div>
                <div class="text-subtitle-2">Initial Reading (kWh)</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="success">mdi-gauge-full</v-icon>
                <div class="text-h4 success--text mt-2">{{ formatNumber(dgBillingRecord.dgConsumptionData.finalReading) }}</div>
                <div class="text-subtitle-2">Final Reading (kWh)</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="primary">mdi-lightning-bolt</v-icon>
                <div class="text-h4 primary--text mt-2">{{ formatNumber(dgBillingRecord.dgConsumptionData.totalConsumption) }}</div>
                <div class="text-subtitle-2">Total Generation (kWh)</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="info">mdi-calculator</v-icon>
                <div class="text-h4 info--text mt-2">{{ averageDailyGeneration }}</div>
                <div class="text-subtitle-2">Avg Daily (kWh/day)</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Fuel and Operations Data -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1 warning--text">
        <v-icon left>mdi-gas-station</v-icon>
        Fuel Consumption & Operations
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="warning">mdi-gas-station</v-icon>
                <div class="text-h4 warning--text mt-2">{{ formatNumber(dgBillingRecord.dieselConsumption) }}</div>
                <div class="text-subtitle-2">Diesel Consumed (Litres)</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="info">mdi-clock</v-icon>
                <div class="text-h4 info--text mt-2">{{ formatNumber(dgBillingRecord.runningHours) }}</div>
                <div class="text-subtitle-2">Running Hours</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="secondary">mdi-speedometer</v-icon>
                <div class="text-h4 secondary--text mt-2">{{ averageHoursPerDay }}</div>
                <div class="text-subtitle-2">Avg Hours/Day</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Efficiency Metrics -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1 secondary--text">
        <v-icon left>mdi-gauge</v-icon>
        Efficiency Metrics
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" :color="getEfficiencyColor(dgBillingRecord.efficiencyMetrics.kwhPerLitre)">mdi-flash</v-icon>
                <div class="text-h3 mt-2" :class="getEfficiencyColor(dgBillingRecord.efficiencyMetrics.kwhPerLitre) + '--text'">
                  {{ formatEfficiency(dgBillingRecord.efficiencyMetrics.kwhPerLitre) }}
                </div>
                <div class="text-subtitle-1">kWh per Litre</div>
                <div class="text-caption grey--text">Power Generation Efficiency</div>
                <v-chip
                  small
                  :color="getEfficiencyColor(dgBillingRecord.efficiencyMetrics.kwhPerLitre)"
                  text-color="white"
                  class="mt-2"
                >
                  {{ getEfficiencyRating(dgBillingRecord.efficiencyMetrics.kwhPerLitre) }}
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-text class="text-center">
                <v-icon size="48" color="info">mdi-clock-fast</v-icon>
                <div class="text-h3 info--text mt-2">{{ formatEfficiency(dgBillingRecord.efficiencyMetrics.litresPerHour) }}</div>
                <div class="text-subtitle-1">Litres per Hour</div>
                <div class="text-caption grey--text">Fuel Consumption Rate</div>
                <v-chip
                  small
                  color="info"
                  text-color="white"
                  class="mt-2"
                >
                  Operational Metric
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Efficiency Analysis -->
        <v-row class="mt-4">
          <v-col cols="12">
            <v-alert
              :type="getEfficiencyAlertType(dgBillingRecord.efficiencyMetrics.kwhPerLitre)"
              outlined
            >
              <div class="text-subtitle-2">Efficiency Analysis</div>
              <div class="text-body-1">{{ getEfficiencyAnalysis(dgBillingRecord.efficiencyMetrics.kwhPerLitre) }}</div>
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Performance Comparison -->
    <v-card class="mb-4" v-if="showPerformanceComparison">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-compare</v-icon>
        Performance Benchmarks
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <div class="text-center">
              <div class="text-subtitle-2">Industry Standard</div>
              <div class="text-h6 success--text">3.0 - 3.5 kWh/L</div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-center">
              <div class="text-subtitle-2">Your Performance</div>
              <div class="text-h6" :class="getEfficiencyColor(dgBillingRecord.efficiencyMetrics.kwhPerLitre) + '--text'">
                {{ formatEfficiency(dgBillingRecord.efficiencyMetrics.kwhPerLitre) }} kWh/L
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-center">
              <div class="text-subtitle-2">Performance Gap</div>
              <div class="text-h6" :class="performanceGapColor + '--text'">
                {{ performanceGap }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Audit Trail -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-history</v-icon>
        Audit Trail
      </v-card-title>

      <v-card-text>
        <v-timeline dense>
          <v-timeline-item
            color="primary"
            small
          >
            <div class="text-subtitle-2">DG Record Created</div>
            <div class="text-caption grey--text">{{ formatDateTime(dgBillingRecord.createdAt) }}</div>
            <div class="text-body-2">Created by {{ dgBillingRecord.createdBy || 'operator' }}</div>
          </v-timeline-item>

          <v-timeline-item
            color="success"
            small
          >
            <div class="text-subtitle-2">DG Consumption Data Retrieved</div>
            <div class="text-caption grey--text">{{ formatDate(dgBillingRecord.billingPeriod.startDate) }} - {{ formatDate(dgBillingRecord.billingPeriod.endDate) }}</div>
            <div class="text-body-2">{{ formatNumber(dgBillingRecord.dgConsumptionData.totalConsumption) }} kWh generated over {{ billingDuration }} days</div>
          </v-timeline-item>

          <v-timeline-item
            color="warning"
            small
          >
            <div class="text-subtitle-2">Fuel Data Entered</div>
            <div class="text-caption grey--text">{{ formatDateTime(dgBillingRecord.createdAt) }}</div>
            <div class="text-body-2">
              Diesel consumption: {{ formatNumber(dgBillingRecord.dieselConsumption) }} litres
              <br>
              Running hours: {{ formatNumber(dgBillingRecord.runningHours) }} hours
            </div>
          </v-timeline-item>

          <v-timeline-item
            :color="getEfficiencyColor(dgBillingRecord.efficiencyMetrics.kwhPerLitre)"
            small
          >
            <div class="text-subtitle-2">Efficiency Calculated</div>
            <div class="text-caption grey--text">{{ formatDateTime(dgBillingRecord.createdAt) }}</div>
            <div class="text-body-2">
              Power efficiency: {{ formatEfficiency(dgBillingRecord.efficiencyMetrics.kwhPerLitre) }} kWh/L
              <br>
              Fuel rate: {{ formatEfficiency(dgBillingRecord.efficiencyMetrics.litresPerHour) }} L/hr
            </div>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>

    <!-- Actions -->
    <v-card>
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-cog</v-icon>
        Actions
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" class="text-center">
            <v-btn
              color="primary"
              class="mr-2"
              @click="exportRecord"
            >
              <v-icon left>mdi-download</v-icon>
              Export Record
            </v-btn>

            <v-btn
              color="info"
              class="mr-2"
              @click="printRecord"
            >
              <v-icon left>mdi-printer</v-icon>
              Print
            </v-btn>

            <v-btn
              color="secondary"
              @click="goBack"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Back to List
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>

  <!-- Loading State -->
  <div v-else-if="loading" class="text-center pa-8">
    <v-progress-circular
      indeterminate
      color="primary"
      size="64"
    />
    <div class="text-h6 mt-4">Loading DG billing record...</div>
  </div>

  <!-- Error State -->
  <div v-else class="text-center pa-8">
    <v-icon size="64" color="error">mdi-alert-circle</v-icon>
    <div class="text-h6 mt-4">DG billing record not found</div>
    <div class="text-body-1 grey--text">The requested DG billing record could not be loaded.</div>
    <v-btn
      color="primary"
      class="mt-4"
      @click="goBack"
    >
      <v-icon left>mdi-arrow-left</v-icon>
      Back to List
    </v-btn>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { DGExportService } from '../../services/dgExportService';

export default {
  name: 'DGBillingDetail',

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapGetters('dgBilling', ['loading']),

    dgBillingRecord() {
      return this.$store.getters['dgBilling/getDGBillingRecordById'](this.id);
    },

    billingDuration() {
      if (!this.dgBillingRecord) return 0;
      const start = new Date(this.dgBillingRecord.billingPeriod.startDate);
      const end = new Date(this.dgBillingRecord.billingPeriod.endDate);
      const diffTime = Math.abs(end - start);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },

    averageDailyGeneration() {
      if (!this.dgBillingRecord || this.billingDuration === 0) return '0.0';
      const avg = this.dgBillingRecord.dgConsumptionData.totalConsumption / this.billingDuration;
      return avg.toFixed(1);
    },

    averageHoursPerDay() {
      if (!this.dgBillingRecord || this.billingDuration === 0) return '0.0';
      const avg = this.dgBillingRecord.runningHours / this.billingDuration;
      return avg.toFixed(1);
    },

    efficiencyStatusColor() {
      const efficiency = this.dgBillingRecord?.efficiencyMetrics?.kwhPerLitre || 0;
      return this.getEfficiencyColor(efficiency);
    },

    efficiencyStatusIcon() {
      const efficiency = this.dgBillingRecord?.efficiencyMetrics?.kwhPerLitre || 0;
      if (efficiency >= 3.5) return 'mdi-check-circle';
      if (efficiency >= 2.5) return 'mdi-alert';
      return 'mdi-alert-circle';
    },

    efficiencyStatusText() {
      const efficiency = this.dgBillingRecord?.efficiencyMetrics?.kwhPerLitre || 0;
      return this.getEfficiencyRating(efficiency);
    },

    showPerformanceComparison() {
      return this.dgBillingRecord?.efficiencyMetrics?.kwhPerLitre > 0;
    },

    performanceGap() {
      if (!this.dgBillingRecord) return 'N/A';
      const efficiency = this.dgBillingRecord.efficiencyMetrics.kwhPerLitre;
      const industryStandard = 3.25; // Mid-point of 3.0-3.5 range
      const gap = efficiency - industryStandard;
      if (gap > 0) {
        return `+${gap.toFixed(2)} kWh/L`;
      }
      return `${gap.toFixed(2)} kWh/L`;
    },

    performanceGapColor() {
      if (!this.dgBillingRecord) return 'grey';
      const efficiency = this.dgBillingRecord.efficiencyMetrics.kwhPerLitre;
      const industryStandard = 3.5;
      return efficiency >= industryStandard ? 'success' : 'error';
    },
  },

  async created() {
    await this.loadDGBillingRecord();
  },

  methods: {
    ...mapActions('dgBilling', ['fetchDGBillingRecords']),

    async loadDGBillingRecord() {
      try {
        await this.fetchDGBillingRecords();

        // Check if the specific record was found
        if (!this.dgBillingRecord) {
          this.$emit('show-message', {
            type: 'warning',
            message: 'DG billing record not found. It may have been deleted or you may not have access to it.',
          });
        }
      } catch (error) {
        console.error('Error loading DG billing record:', error);

        let errorMessage = 'Failed to load DG billing record';

        if (error.message) {
          if (error.message.includes('network') || error.message.includes('connection')) {
            errorMessage = 'Network connection failed. Please check your internet connection.';
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Request timed out. Please try again.';
          } else if (error.message.includes('permission') || error.message.includes('403')) {
            errorMessage = 'Access denied. You do not have permission to view this record.';
          } else if (error.message.includes('not found') || error.message.includes('404')) {
            errorMessage = 'DG billing record not found. It may have been deleted.';
          } else if (error.message.length < 100) {
            errorMessage = error.message;
          }
        }

        this.$emit('show-message', {
          type: 'error',
          message: errorMessage,
        });
      }
    },

    exportRecord() {
      try {
        if (!this.dgBillingRecord) {
          throw new Error('No DG billing record available to export');
        }

        // Validate record has required data
        if (!this.dgBillingRecord.billingPeriod || !this.dgBillingRecord.dieselConsumption) {
          throw new Error('DG billing record is missing required data for export');
        }

        DGExportService.exportDGRecordToCSV(this.dgBillingRecord);
        this.$emit('show-message', {
          type: 'success',
          message: 'DG record exported successfully',
        });
      } catch (error) {
        console.error('Error exporting DG record:', error);

        let errorMessage = 'Failed to export DG record';
        if (error.message && error.message.length < 100) {
          errorMessage = error.message;
        }

        this.$emit('show-message', {
          type: 'error',
          message: errorMessage,
        });
      }
    },

    editRecord() {
      const recordId = this.dgBillingRecord._id || this.dgBillingRecord.id;
      this.$router.push(`/dg-billing-history/${recordId}/edit`);
    },

    printRecord() {
      if (!this.dgBillingRecord) {
        this.$emit('show-message', {
          type: 'error',
          message: 'No DG billing record data available for printing',
        });
        return;
      }

      try {
        // Create a print-friendly version of the record
        const printContent = this.generatePrintContent();

        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=800,height=600');

        if (!printWindow) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Unable to open print window. Please check your browser popup settings.',
          });
          return;
        }

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>DG Billing Record - ${this.dgBillingRecord.id || this.dgBillingRecord._id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            .section-title {
              font-size: 16px;
              font-weight: bold;
              color: #1976d2;
              border-bottom: 1px solid #ddd;
              padding-bottom: 5px;
              margin-bottom: 10px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              padding: 4px 0;
            }
            .info-label {
              font-weight: bold;
              width: 40%;
            }
            .info-value {
              width: 60%;
            }
            .efficiency-metrics {
              background-color: #f5f5f5;
              padding: 15px;
              border-radius: 5px;
            }
            .notes {
              background-color: #fff3cd;
              padding: 10px;
              border-left: 4px solid #ffc107;
              margin-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

        printWindow.document.close();

        // Wait for content to load then print
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      } catch (error) {
        console.error('Error printing DG record:', error);
        this.$emit('show-message', {
          type: 'error',
          message: 'Failed to print DG record. Please try again.',
        });
      }
    },

    generatePrintContent() {
      const record = this.dgBillingRecord;

      if (!record) {
        return '<div class="error">No DG billing record data available for printing.</div>';
      }

      return `
        <div class="header">
          <h1>DG Billing Record</h1>
          <h2>Record ID: ${record.id || record._id}</h2>
          <p>Generated on: ${new Date().toLocaleString()}</p>
        </div>

        <div class="section">
          <div class="section-title">Billing Period</div>
          <div class="info-row">
            <span class="info-label">Start Date:</span>
            <span class="info-value">${this.formatDate(record.billingPeriod.startDate)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">End Date:</span>
            <span class="info-value">${this.formatDate(record.billingPeriod.endDate)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Duration:</span>
            <span class="info-value">${this.billingDuration} days</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">DG Consumption Data</div>
          <div class="info-row">
            <span class="info-label">Initial Reading:</span>
            <span class="info-value">${record.dgConsumptionData.initialReading} kWh</span>
          </div>
          <div class="info-row">
            <span class="info-label">Final Reading:</span>
            <span class="info-value">${record.dgConsumptionData.finalReading} kWh</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Consumption:</span>
            <span class="info-value">${record.dgConsumptionData.totalConsumption} kWh</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Fuel & Operations Data</div>
          <div class="info-row">
            <span class="info-label">Diesel Consumption:</span>
            <span class="info-value">${record.dieselConsumption} Litres</span>
          </div>
          <div class="info-row">
            <span class="info-label">Running Hours:</span>
            <span class="info-value">${record.runningHours} hours</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Efficiency Metrics</div>
          <div class="efficiency-metrics">
            <div class="info-row">
              <span class="info-label">kWh per Litre:</span>
              <span class="info-value">${record.efficiencyMetrics.kwhPerLitre.toFixed(2)} kWh/L</span>
            </div>
            <div class="info-row">
              <span class="info-label">Litres per Hour:</span>
              <span class="info-value">${record.efficiencyMetrics.litresPerHour.toFixed(2)} L/hr</span>
            </div>
            <div class="info-row">
              <span class="info-label">Efficiency Rating:</span>
              <span class="info-value">${this.getEfficiencyRating(record.efficiencyMetrics.kwhPerLitre)}</span>
            </div>
          </div>
        </div>

        ${record.notes
    ? `
        <div class="section">
          <div class="section-title">Notes</div>
          <div class="notes">
            ${record.notes}
          </div>
        </div>
        `
    : ''}

        <div class="section">
          <div class="section-title">Record Information</div>
          <div class="info-row">
            <span class="info-label">Created By:</span>
            <span class="info-value">${record.createdBy || 'System'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Created At:</span>
            <span class="info-value">${this.formatDateTime(record.createdAt)}</span>
          </div>
          ${record.updatedAt
    ? `
          <div class="info-row">
            <span class="info-label">Last Updated:</span>
            <span class="info-value">${this.formatDateTime(record.updatedAt)}</span>
          </div>
          `
    : ''}
        </div>
      `;
    },

    goBack() {
      this.$router.push('/dg-billing-history');
    },

    // Formatting methods
    formatNumber(value) {
      return value ? value.toLocaleString() : '0';
    },

    formatEfficiency(value) {
      return value ? value.toFixed(2) : '0.00';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    formatDateTime(date) {
      return new Date(date).toLocaleString();
    },

    // Efficiency analysis methods
    getEfficiencyColor(kwhPerLitre) {
      if (kwhPerLitre >= 3.5) return 'success';
      if (kwhPerLitre >= 2.5) return 'warning';
      return 'error';
    },

    getEfficiencyRating(kwhPerLitre) {
      if (kwhPerLitre >= 3.5) return 'Excellent';
      if (kwhPerLitre >= 3.0) return 'Good';
      if (kwhPerLitre >= 2.5) return 'Fair';
      return 'Poor';
    },

    getEfficiencyAlertType(kwhPerLitre) {
      if (kwhPerLitre >= 3.5) return 'success';
      if (kwhPerLitre >= 2.5) return 'warning';
      return 'error';
    },

    getEfficiencyAnalysis(kwhPerLitre) {
      if (kwhPerLitre >= 3.5) {
        return 'Excellent efficiency! Your DG is performing above industry standards. This indicates optimal fuel utilization and well-maintained equipment.';
      } if (kwhPerLitre >= 3.0) {
        return 'Good efficiency within industry standards. Your DG is performing well with room for minor optimization.';
      } if (kwhPerLitre >= 2.5) {
        return 'Fair efficiency below optimal range. Consider maintenance checks, load optimization, or fuel quality assessment.';
      }
      return 'Poor efficiency significantly below standards. Immediate attention required - check for maintenance issues, fuel quality, or equipment problems.';
    },
  },
};
</script>

<style scoped>
.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
